import React from "react"
import { useId } from "@reach/auto-id"
import { default as ReactSelect } from "react-select"
import { useTranslation } from "react-i18next"

import { handleInternalInputValidation } from "../formUtils"
import { Errors } from "../Errors"

import "./Select.scoped.scss"
import { isNotEmpty } from "utils/validation"

export const Select = ({
    // Attributes
    id,
    value,
    options,
    name,
    label = "",
    placeholder = "",
    defaultValue,
    disabled,
    readonly = false,
    // Styles
    style,
    className,
    classNameLabel = "",
    selectClassname,
    // Error management
    required = false,
    customRules,
    localValidation,
    showErrors,
    // Handler
    onChange,
    onValidate
}) => {
    const [selectedOption, setSelectedOption] = React.useState(defaultValue)
    const [errors, setErrors] = React.useState([])
    const { t } = useTranslation()

    const prefix = useId()
    const inputId = id ?? `${prefix}-${name}`
    const errorId = `${inputId}-error`

    React.useEffect(() => {
        if (value?.value) handleChange(value)
    }, [value]) // eslint-disable-line

    /**
     * Observe if default value was updated.
     * If default value should be update on parent component did mount, we probably
     * must update the default value.
     */
    React.useEffect(() => {
        if (defaultValue) setSelectedOption(defaultValue)
    }, [defaultValue])

    const rules = React.useMemo(() => [
        {
            key: "select-empty",
            check: (value) => required ? isNotEmpty(value) : true,
            message: t("error_mandatory_field", { ns: "form" })
        },
        {
            key: "select-invalid",
            check: (value) => (isNotEmpty(value) && isNotEmpty(localValidation)) ? localValidation : true,
            message: t("error_invalid_value", { ns: "form" })
        },
        ...(customRules || [])
    ], [required, localValidation, t]) // eslint-disable-line
    // customRules in dependencies array trigger an infinite loop,
    // because custom rules include a check property defining a new function,
    // preventing useMemo to return a valid shallow-comparison
    // or event useDeepMemo to return a valid deep-comparison

    const validate = React.useCallback(
        (currentValue) =>
            handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
        [rules, name, onValidate]
    )

    // validate on mount and each time value change
    React.useEffect(() => {
        validate(value)
        return () => {
            // On unmounted erros and validation are no longer needed
            setErrors([])
            if (onValidate) onValidate(name, [])
        }
    }, [value, validate, onValidate, name])

    const handleChange = (event) => {
        const inputValue = {
            target: {
                name: name,
                value: event.value,
                label: event.label
            }
        }
        setSelectedOption(inputValue.target)
        onChange(inputValue)
    }

    // Transform label with i18n if label constains ":" like => label = "bike:bike_types.child_bike"
    const formatOptionLabel = ({ label }) => label?.indexOf(":") !== -1 ? t(`${label}`) : label

    return (
        <>
            <div className={"c-form-group " + (className)} style={style}>
                <label className={"c-label mb-1 " + classNameLabel} htmlFor={inputId}>{label} {required && "*"}</label>
                <ReactSelect
                    options={options}
                    formatOptionLabel={formatOptionLabel}
                    value={selectedOption}
                    inputId={inputId}
                    isDisabled={disabled || readonly}
                    placeholder={placeholder}
                    className={"c-select " + (selectClassname ? selectClassname : "")}
                    classNamePrefix={"c-select"}
                    onChange={(e) => !readonly && handleChange(e)}
                    onFocus={() => validate(selectedOption)}
                    onBlur={() => validate(selectedOption?.value)}
                    aria-describedby={errorId}
                    aria-invalid={Boolean(errors.length)}
                    aria-live="polite"
                />

                {/* Error messages */}
                {showErrors && <Errors id={errorId} errors={errors} />}
            </div>
        </>
    )
}
